import {ContentEditableTextEditor, TextEditorServices} from '@frontapp/editor-html';
import {Trans} from '@lingui/macro';
import React, {FC, RefObject, useEffect, useRef} from 'react';
import {Key} from 'ts-key-enum';

import {ServiceSubscription} from '../../../../../../../src/components/common/editor/serviceSubscription';
import {SearchBar} from '../../../../../../../src/components/common/searchBar/searchBar';
import {useSearchBarEditor} from '../../../../../../../src/components/common/searchBar/searchBarEditor';
import {
  buildContactsManagerCompanyContactsUrl,
  buildContactsManagerSearchUrl,
} from '../../../../../../../src/helpers/contactsManagerHelpers';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../src/services/tracking';
import {
  ContactsManagerParameters,
  ContactsManagerParametersTypesEnum,
  ContactsManagerSearchTabsEnum,
} from '../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {usePrevious} from '../../../../../../../srcCommon/helpers/hookHelpers';
import {
  FrontKeyboardEvent,
  Handler,
  HandlerOf,
} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {ContactsManagerSearchDropdown} from './contactsManagerSearchDropdown';

/*
 * Props.
 */

interface ContactsManagerSearchBarProps {
  query: string;
  parameters: ContactsManagerParameters;
  navigateToLink: HandlerOf<string>;
}

/*
 * Component.
 */

export const ContactsManagerSearchBar: FC<ContactsManagerSearchBarProps> = (props) => {
  const {query, parameters, navigateToLink} = props;
  const editor = useSearchBarEditor(query);
  const searchBarWrapperRef = useRef<HTMLDivElement>(null);
  const previousParameterType = usePrevious(parameters.type);

  // Watches for if we navigate from the search page so we can clear the search text.
  useEffect(() => {
    if (
      previousParameterType === ContactsManagerParametersTypesEnum.SEARCH &&
      parameters.type !== ContactsManagerParametersTypesEnum.SEARCH
    ) {
      editor.services.text.setText('');
    }
  }, [editor.services.text, parameters.type, previousParameterType]);

  const onNavigateToQuery = () => {
    if (query !== editor.services.text.text) {
      navigateToLink(
        buildContactsManagerSearchUrl(
          editor.services.text.text,
          computePreSelectedTabFromRouteType(parameters.type),
        ),
      );
    }
    trackInterfaceEvent(InterfaceEventsEnum.P0_CONTACTS_MANAGER_SEARCH);
    editor.services.focus.blur();
  };

  const onKeyDown = (event: FrontKeyboardEvent) => {
    switch (event.key) {
      case Key.Enter:
        event.preventDefault();
        onNavigateToQuery();

        break;

      case Key.Escape:
        event.preventDefault();
        editor.services.focus.blur();
        break;

      default:
      // Do nothing.
    }
  };

  const onClear = () => {
    editor.services.text.setText('');
    if (parameters.type === ContactsManagerParametersTypesEnum.SEARCH) {
      navigateToLink(buildContactsManagerCompanyContactsUrl());
    }
  };

  // On a click on the box, focus the input.
  const onBoxClick = () => editor.services.focus.focus();

  return (
    <ServiceSubscription source={editor.services.focus}>
      {({isActive}) => (
        <>
          <SearchBar
            editor={editor}
            outerRef={searchBarWrapperRef}
            isFocused={isActive}
            onKeyDown={onKeyDown}
            renderPlaceholder={renderPlaceholder}
            onExitClick={onClear}
            onClick={onBoxClick}
          />
          {maybeRenderSuggestionsDropdown(isActive, searchBarWrapperRef, editor, onNavigateToQuery)}
        </>
      )}
    </ServiceSubscription>
  );
};

/*
 * Helpers.
 */

function renderPlaceholder() {
  return <Trans id="contacts-manager-search-bar-placeholder">Search accounts, contacts, or lists</Trans>;
}

function maybeRenderSuggestionsDropdown(
  isFocused: boolean,
  anchor: RefObject<HTMLDivElement>,
  editor: ContentEditableTextEditor<TextEditorServices>,
  onNavigateToQuery: Handler,
) {
  if (!isFocused) {
    return null;
  }

  return (
    <ServiceSubscription source={editor.services.textUnderCursor}>
      {({textUnderCursor}) => (
        <ContactsManagerSearchDropdown
          anchor={anchor}
          query={textUnderCursor.text}
          onQuerySelect={onNavigateToQuery}
        />
      )}
    </ServiceSubscription>
  );
}

function computePreSelectedTabFromRouteType(type: ContactsManagerParametersTypesEnum) {
  switch (type) {
    case ContactsManagerParametersTypesEnum.ACCOUNTS:
    case ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS:
      return ContactsManagerSearchTabsEnum.ACCOUNTS;
    case ContactsManagerParametersTypesEnum.LISTS:
    case ContactsManagerParametersTypesEnum.LIST_CONTACTS:
      return ContactsManagerSearchTabsEnum.CARD_GROUPS;
    case ContactsManagerParametersTypesEnum.COMPANY_CONTACTS:
    case ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS:
    case ContactsManagerParametersTypesEnum.OTHER_CONTACTS:
    default:
      return ContactsManagerSearchTabsEnum.CONTACTS;
  }
}
